import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCreditHistory } from '../../redux/ReduxSlices/authSlice';
import { useNavigate } from 'react-router';
import { FiArrowLeft } from 'react-icons/fi';

const AssessmentHistory = () => {
    const { creditData } = useSelector((state) => state.auth);
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const [activeIndex, setActiveIndex] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    useEffect(() => {
        dispatch(getCreditHistory())
    }, [dispatch]);


    const filteredUsers = useMemo(() => {
        if (!searchTerm) return creditData;
        const term = searchTerm.toLowerCase();
        return creditData.filter(user =>
            user.name?.toLowerCase().includes(term) ||
            user.email?.toLowerCase().includes(term) ||
            user.user_type?.toLowerCase().includes(term)
        );
    }, [creditData, searchTerm]);

    const toggleAccordion = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getUserTypeColor = (userType) => {
        switch (userType) {
            case 'Super Admin': return 'bg-purple-100 text-purple-800';
            case 'Site Admin': return 'bg-blue-100 text-blue-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className=" mx-auto  sm:p-4 md:p-8">
            {/* Header with Search */}
            <div className="px-6 pt-4 max-md:px-4 max-md:pt-0 ">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                    <div className='max-md:flex max-md:gap-4 max-md:mt-16'>
                        <button
                            onClick={() => navigate(-1)}
                            className="flex justify-center items-center gap-2"
                        >
                            <FiArrowLeft className=" text-xl" /> <span>Back</span>
                        </button>
                        <div className='mt-2'>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Assessment History</h1>
                            <p className="text-xs sm:text-sm text-gray-600">Click on user cards to view details</p>
                        </div>
                    </div>
                    <div className="relative w-full md:w-64">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <input
                            type="text"
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* User Cards */}
            <div className='w-full flex justify-center'>
                <div className="w-full max-md:w-full space-y-2 sm:space-y-4">
                    {filteredUsers?.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            No users found matching your search
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-4 items-start [grid-auto-rows:min-content] max-md:flex max-md:flex-col max-md:items-center max-md:w-full p-4">
                            {filteredUsers?.map((user, index) => (
                                <div
                                    key={index}
                                    className={`bg-white max-md:w-full rounded-lg sm:rounded-xl shadow-sm sm:shadow-md overflow-hidden transition-all duration-200 ${activeIndex === index ? 'ring-1 ring-violet-500' : 'hover:shadow-md'
                                        }`}
                                >
                                    {/* User Header */}
                                    <div
                                        className="flex items-center p-3 sm:p-4 cursor-pointer"
                                        onClick={() => toggleAccordion(index)}
                                    >
                                        <div className="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-violet-500 flex items-center justify-center text-white font-bold text-sm sm:text-base">
                                            {user.name.charAt(0)}
                                        </div>

                                        <div className="ml-3 flex-1 min-w-0">
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2">
                                                <h2 className="text-sm sm:text-base font-semibold text-gray-800 truncate">
                                                    {user.name}
                                                </h2>
                                                <p className="text-xs text-gray-500 truncate sm:max-w-[180px]">
                                                    {user.email}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center space-x-3 ml-2">
                                            <span className="hidden sm:inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                                                {user.sent_assessments} assessments
                                            </span>
                                            <span className={`hidden md:inline-flex px-3 py-1 ${getUserTypeColor(user.user_type)} text-xs font-medium rounded-full`}>
                                                {user.user_type}
                                            </span>
                                            <svg
                                                className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 transition-transform duration-200 ${activeIndex === index ? 'transform rotate-180' : ''
                                                    }`}
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>

                                    {/* Expanded Content */}
                                    {activeIndex === index && (
                                        <div className="border-t border-gray-100 p-2 sm:p-4 bg-gray-50 transition-all duration-200">
                                            <div className="mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                                <div className="flex items-center mb-2 sm:mb-0">
                                                    <span className={`md:hidden px-2 py-0.5 ${getUserTypeColor(user.user_type)} text-xs font-medium rounded-full mr-2`}>
                                                        {user.user_type}
                                                    </span>
                                                    <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                                                        <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                                        </svg>
                                                        Assessment Details
                                                    </h3>
                                                </div>
                                                <span className="text-xs text-gray-500">
                                                    Last sent: {formatDate(user.last_sent)}
                                                </span>
                                            </div>

                                            <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 max-h-96 overflow-y-auto">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-gray-100 sticky top-0">
                                                        <tr>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                                                Recipient
                                                            </th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                                                Date Sent
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {user.data.map((item, itemIndex) => (
                                                            <tr key={itemIndex} className="hover:bg-gray-50">
                                                                <td className="px-3 py-2 whitespace-nowrap">
                                                                    <div className="flex items-center">
                                                                        <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                                        </svg>
                                                                        <span className="text-xs sm:text-sm text-gray-800 truncate max-w-[120px] sm:max-w-xs">
                                                                            {item.email}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs sm:text-sm text-gray-600">
                                                                    {formatDate(item.date_time)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AssessmentHistory;